"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.getMidnightOfUtcDay = getMidnightOfUtcDay;
exports.getUtcDisplayDate = getUtcDisplayDate;
exports.floorToUtcMidnight = floorToUtcMidnight;
exports.isSameUtcCalendarDay = isSameUtcCalendarDay;
exports.startOfUtcYear = startOfUtcYear;
exports.addUtcDays = addUtcDays;
exports.utcDaysBetween = utcDaysBetween;
exports.getDurationDays = getDurationDays;
const luxon_1 = require("luxon");
/**
 * Gets the date at the next midnight i.e. the start of the day in UTC
 * e.g. 2023-03-19T17:00:00.000-7:00 becomes 2023-03-20T00:00:00.000-7:00
 * @param date
 * @returns The datetime + the time offset from UTC
 */
function getMidnightOfUtcDay(date) {
    const newDate = new Date(date);
    // This is a workaround to the date being converted to the local timezone automatically
    // and then displaying in a datepicker as 1 day prior to the date in the database.
    // This sets the time to midnight i.e. the start of the UTC day. For example,
    // 2023-03-20 might get converted to 2023-03-19T17:00:00.000-7:00 if you're in PDT.
    // We want to set the time to 2023-03-20T00:00:00.000-7:00 so that it displays correctly.
    newDate.setMinutes(newDate.getMinutes() + newDate.getTimezoneOffset());
    return newDate;
}
/**
 * Uses Luxon to get a string representation of the date only (without the timestamp) in the UTC timezone
 * @param date
 * @param type either "short" to get the format MM/dd/yyyy or "full" to get the format MMM d, yyyy. Defaults to "short".
 * @returns formatted string of the UTC date with no timestamp
 */
function getUtcDisplayDate(date, type = "short") {
    const inputDate = typeof date === "string" ? new Date(date) : date;
    const formatTypes = {
        short: luxon_1.DateTime.DATE_SHORT,
        med: luxon_1.DateTime.DATE_MED,
        full: luxon_1.DateTime.DATE_FULL,
    };
    return luxon_1.DateTime.fromJSDate(inputDate, {
        zone: "utc",
    }).toLocaleString(formatTypes[type]);
}
/**
 * Floors an instant to the start of its UTC day (00:00:00.000Z).
 * e.g. 2026-03-20T17:45:12.345-07:00 becomes 2026-03-21T00:00:00.000Z
 * (that instant is already March 21 in UTC).
 *
 * This is the floor in **UTC**, independent of the machine's timezone —
 * use it for `@db.Date` columns and calendar-day comparisons, where a
 * system-zone floor would diverge between a dev laptop and a Lambda.
 * @param date
 * @returns a new Date at the start of the input's UTC day
 */
function floorToUtcMidnight(date) {
    return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()));
}
/**
 * Whether two instants fall on the same UTC calendar day. Use for
 * "did the date change?" checks on date-only (`@db.Date` / UTC-midnight)
 * values, where local-zone getters would disagree with the stored day on
 * any runtime west of UTC.
 * @param a
 * @param b
 * @returns true when both instants share a UTC calendar day
 */
function isSameUtcCalendarDay(a, b) {
    return floorToUtcMidnight(a).getTime() === floorToUtcMidnight(b).getTime();
}
/**
 * Start of a date's year in UTC (Jan 1 00:00:00.000Z). Defaults to the
 * current year. Use for year-to-date windows over UTC-midnight date
 * columns — a local-zone Jan 1 lower bound (e.g.
 * `new Date(year, 0, 1)`) sits hours past UTC midnight on US runtimes
 * and silently drops exactly-Jan-1 rows.
 * @param date the instant whose UTC year anchors the result
 * @returns a new Date at Jan 1 00:00:00.000Z of that UTC year
 */
function startOfUtcYear(date = new Date()) {
    return new Date(Date.UTC(date.getUTCFullYear(), 0, 1));
}
/**
 * Adds whole calendar days in UTC, without mutating the input.
 * Negative values subtract. Uses `setUTCDate` rather than millisecond
 * arithmetic so the contract stays exact for any input time-of-day.
 * @param date
 * @param days whole days to add (may be negative)
 * @returns a new Date `days` UTC days after the input
 */
function addUtcDays(date, days) {
    const result = new Date(date);
    result.setUTCDate(result.getUTCDate() + days);
    return result;
}
/**
 * Whole UTC calendar days from `from` to `to`, comparing their UTC
 * midnights — positive when `to` is later, negative when earlier, zero for
 * two instants on the same UTC day. Time-of-day never affects the answer.
 * @param from
 * @param to
 * @returns signed whole days between the two dates' UTC days
 */
function utcDaysBetween(from, to) {
    const msPerDay = 24 * 60 * 60 * 1000;
    return Math.round((floorToUtcMidnight(to).getTime() - floorToUtcMidnight(from).getTime()) / msPerDay);
}
// Calculates the number of days (rounded) between two dates
// Returns null if startDate is after endDate (invalid duration)
function getDurationDays(startDate, endDate) {
    const durationMs = endDate.getTime() - startDate.getTime();
    // Return null for invalid durations (endDate before startDate)
    if (durationMs < 0) {
        return null;
    }
    return Math.round(durationMs / (1000 * 60 * 60 * 24));
}
//# sourceMappingURL=date.js.map